import { Input, Form, Modal, Typography, Col, Row, Radio } from "antd";
import MaskedInput from "antd-mask-input";
import { useState, useEffect } from "react";

import CustomSelect from "@/components/inputs/CustomSelect";

import { createItem, updateItem } from "@/api/employees";

import { validateInn } from "@/utils/validations";
import { formateDate, formateStrToISODate } from "@/utils";

const { Text, Paragraph } = Typography;

// Окно для создания/редактирования сотрудника, содержит форму
const EmployeeModal = ({
  data = {},
  units = [],
  positions = [],
  visible,
  onSave,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Т.к. могут прийти uuid удаленных отделов или должностей
  useEffect(() => {
    const checkAndResetField = (fieldName, options) => {
      const currentValue = form.getFieldValue(fieldName);
      if (
        currentValue &&
        !options.some((option) => option.uuid === currentValue)
      ) {
        form.setFieldsValue({ [fieldName]: null });
      }
    };

    checkAndResetField("position_uuid", positions);
    checkAndResetField("unit_uuid", units);
  }, [positions, units, form]);

  // Т.к. нужно отформатировать данные пользователя
  const formatedData = (data) => {
    if (!data) return {};

    return {
      ...data,
      birthdate: formateDate(data.birthdate),
      employmentdate: formateDate(data.employmentdate),
      dismissaldate: formateDate(data.dismissaldate),
    };
  };

  const handleCancel = () => {
    form.resetFields();
    setError(null);
    onCancel();
  };

  const handleSave = async (values) => {
    setError(null);
    setIsLoading(true);

    const res = {
      ...values,
      birthdate: formateStrToISODate(values.birthdate),
      employmentdate: formateStrToISODate(values.employmentdate),
      dismissaldate: formateStrToISODate(values.dismissaldate),
    };

    try {
      if (data) {
        await updateItem({
          ...data,
          ...res,
        });
      } else {
        await createItem(res);
      }

      onSave();
      form.resetFields();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onOk = () => {
    form
      .validateFields()
      .then((values) => {
        handleSave(values);
      })
      .catch((info) => {
        console.log("Ошибка валидации:", info);
      });
  };

  return (
    <Modal
      maskClosable={false}
      title={
        data ? "Редактирование сотрудника" : "Добавление нового сотрудника"
      }
      open={visible}
      onOk={onOk}
      onCancel={handleCancel}
      okText={data ? "Сохранить" : "Добавить"}
      cancelText="Отмена"
      okButtonProps={{ loading: isLoading }}
      width={1000}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={formatedData(data)}
        onFinish={handleSave}
      >
        <Paragraph
          style={{
            fontWeight: "600",
            fontSize: "14px",
            color: "rgba(44, 49, 72, 0.45)",
          }}
        >
          Персональные данные
        </Paragraph>

        <Row gutter={20}>
          <Col span={8}>
            <Form.Item
              name="lastname"
              label="Фамилия"
              rules={[
                { required: true, message: "Пожалуйста, введите фамилию" },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="firstname"
              label="Имя"
              rules={[{ required: true, message: "Пожалуйста, введите имя" }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="patronymic" label="Отчество">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={20}>
          <Col span={8}>
            <Form.Item name="birthdate" label="Дата рождения">
              <MaskedInput mask={Date} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item name="phone" label="Контактные данные">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="gender" label="Пол">
              <Radio.Group>
                <Radio value="female">Женский</Radio>
                <Radio value="male">Мужской</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={20}>
          <Col span={8}>
            <Form.Item
              name="inn"
              label="ИНН"
              rules={[
                {
                  validator: validateInn,
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item name="document" label="Документ">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Paragraph
          style={{
            fontWeight: "600",
            fontSize: "14px",
            color: "rgba(44, 49, 72, 0.45)",
          }}
        >
          Информация о сотруднике
        </Paragraph>

        <Row gutter={20}>
          <Col span={8}>
            <Form.Item name="employmentdate" label="Дата приема на работу">
              <MaskedInput mask={Date} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item name="dismissaldate" label="Дата увольнения">
              <MaskedInput mask={Date} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={20}>
          <Col span={8}>
            <Form.Item name="table_number" label="Табельный номер">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="unit_uuid"
              label="Подразделение"
              rules={[
                { required: true, message: "Пожалуйста, выберите отдел" },
              ]}
            >
              <CustomSelect
                options={units}
                showSearch
                fieldNames={{ label: "name", value: "uuid" }}
                optionFilterProp="name"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="position_uuid" label="Должность">
              <CustomSelect
                options={positions}
                showSearch
                fieldNames={{ label: "name", value: "uuid" }}
                optionFilterProp="name"
                allowClear
                onChange={(value) => {
                  if (value === undefined) {
                    form.setFieldsValue({ position_uuid: null });
                  }
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        {error && <Text type="danger">{error}</Text>}
      </Form>
    </Modal>
  );
};

export default EmployeeModal;
